import React, { useState, useEffect } from "react";
import {
  makeStyles,
  Paper,
  Typography,
  Box,
  Grid,
  Card,
  CardContent,
  IconButton,
  Chip,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
} from "@material-ui/core";
import {
  LocalShipping as ShippingIcon,
  Assignment as AssignmentIcon,
  CheckCircle as CheckCircleIcon,
  HourglassEmpty as HourglassIcon,
  Error as ErrorIcon,
  Refresh as RefreshIcon,
  TrendingUp as TrendingUpIcon,
  Speed as SpeedIcon,
  Timeline as TimelineIcon,
} from "@material-ui/icons";
import { i18n } from "../../translate/i18n";
import { DatePicker } from "@material-ui/pickers";
import { Chart as ChartJS, CategoryScale, LinearScale, PointElement, LineElement, Title, Tooltip, Legend, ArcElement } from 'chart.js';
import { Line, Doughnut } from 'react-chartjs-2';
import MainContainer from "../../components/MainContainer";
import MainHeader from "../../components/MainHeader";
import MainHeaderButtonsWrapper from "../../components/MainHeaderButtonsWrapper";
import PageTitle from "../../components/Title";
import { toast } from "react-toastify";
import logisticsService from "../../services/logistics";
import NappNotConfigured from "../../components/NappNotConfigured";
import useNappConfig from "../../hooks/useNappConfig";

ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, Title, Tooltip, Legend, ArcElement);

const useStyles = makeStyles((theme) => ({
  mainPaper: {
    flex: 1,
    padding: theme.spacing(2),
    overflowY: "scroll",
    ...theme.scrollbarStyles,
  },
  statsCard: {
    height: "100%",
    display: "flex",
    flexDirection: "column",
    justifyContent: "space-between",
    background: `linear-gradient(135deg, ${theme.palette.primary.main}15 0%, ${theme.palette.primary.main}05 100%)`,
    border: `1px solid ${theme.palette.primary.main}30`,
  },
  statsContent: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
  },
  statsIcon: {
    fontSize: 40,
    opacity: 0.8,
  },
  statsValue: {
    fontSize: "2rem",
    fontWeight: "bold",
    color: theme.palette.primary.main,
  },
  statsLabel: {
    color: theme.palette.text.secondary,
    marginTop: theme.spacing(1),
  },
  chartCard: {
    height: 400,
    padding: theme.spacing(2),
  },
  chartContainer: {
    position: "relative",
    height: "300px",
    marginTop: theme.spacing(2),
  },
  performanceCard: {
    padding: theme.spacing(2),
  },
  performanceGrid: {
    marginTop: theme.spacing(2),
  },
  performanceItem: {
    textAlign: "center",
    padding: theme.spacing(1),
  },
  performanceValue: {
    fontSize: "1.5rem",
    fontWeight: "bold",
    marginBottom: theme.spacing(0.5),
  },
  performanceLabel: {
    fontSize: "0.875rem",
    color: theme.palette.text.secondary,
  },
  filtersContainer: {
    marginBottom: theme.spacing(3),
    padding: theme.spacing(2),
    backgroundColor: theme.palette.background.default,
  },
  filterRow: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(2),
    flexWrap: "wrap",
  },
  loadingContainer: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    minHeight: 400,
  },
  providerChip: {
    margin: theme.spacing(0.5),
  },
  noData: {
    textAlign: "center",
    padding: theme.spacing(4),
    color: theme.palette.text.secondary,
  },
}));

const LogisticsPage = () => {
  const classes = useStyles();
  const { nappConfig, loading: nappLoading } = useNappConfig();

  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [filters, setFilters] = useState({
    dateFrom: new Date(Date.now() - 30 * 24 * 60 * 60 * 1000), // 30 dias atrás
    dateTo: new Date(),
    period: '30days'
  });

  useEffect(() => {
    loadStats();
  }, [filters.dateFrom, filters.dateTo]);

  const loadStats = async (showRefreshing = false) => {
    try {
      if (showRefreshing) setRefreshing(true);
      else setLoading(true);

      const data = await logisticsService.getLogisticsStats({
        dateFrom: filters.dateFrom?.toISOString().split('T')[0],
        dateTo: filters.dateTo?.toISOString().split('T')[0]
      });

      setStats(data);
    } catch (error) {
      console.error("Erro ao carregar estatísticas:", error);
      toast.error(i18n.t("logistics.stats.fetchError"));
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  const handleRefresh = () => {
    loadStats(true);
  };

  const handlePeriodChange = (period) => {
    let dateFrom = new Date();
    let dateTo = new Date();

    switch (period) {
      case '7days':
        dateFrom.setDate(dateTo.getDate() - 7);
        break;
      case '30days':
        dateFrom.setDate(dateTo.getDate() - 30);
        break;
      case '90days':
        dateFrom.setDate(dateTo.getDate() - 90);
        break;
      case 'thisMonth':
        dateFrom.setDate(1);
        break;
      case 'lastMonth':
        dateFrom.setMonth(dateTo.getMonth() - 1);
        dateFrom.setDate(1);
        dateTo = new Date(dateTo.getFullYear(), dateTo.getMonth(), 0);
        break;
      default:
        return;
    }

    setFilters(prev => ({
      ...prev,
      period,
      dateFrom,
      dateTo
    }));
  };

  // Verificar se NAPP está configurado
  if (nappLoading) {
    return (
      <MainContainer>
        <div className={classes.loadingContainer}>
          <CircularProgress />
        </div>
      </MainContainer>
    );
  }

  if (!nappConfig?.isConfigured) {
    return (
      <MainContainer>
        <NappNotConfigured />
      </MainContainer>
    );
  }

  // Dados para gráficos
  const deliveryStatusData = stats ? {
    labels: [
      i18n.t("logistics.stats.onTime"),
      i18n.t("logistics.stats.delayed"),
      i18n.t("logistics.stats.failed")
    ],
    datasets: [
      {
        data: [
          stats.deliveryPerformance.onTime,
          stats.deliveryPerformance.delayed,
          stats.deliveryPerformance.failed
        ],
        backgroundColor: ['#4caf50', '#ff9800', '#f44336'],
        borderWidth: 0,
      }
    ]
  } : null;

  const providerLabels = stats ? Object.keys(stats.byProvider) : [];
  const providerData = stats ? Object.values(stats.byProvider) : [];
  const providerColors = ['#2196f3', '#4caf50', '#ff9800', '#9c27b0', '#f44336', '#00bcd4', '#8bc34a'];

  const providerChartData = stats && providerLabels.length > 0 ? {
    labels: providerLabels,
    datasets: [
      {
        data: providerData,
        backgroundColor: providerColors.slice(0, providerLabels.length),
        borderWidth: 0,
      }
    ]
  } : null;

  return (
    <MainContainer>
      <MainHeader>
        <PageTitle>{i18n.t("logistics.title")}</PageTitle>
        <MainHeaderButtonsWrapper>
          <IconButton onClick={handleRefresh} disabled={refreshing}>
            {refreshing ? <CircularProgress size={20} /> : <RefreshIcon />}
          </IconButton>
        </MainHeaderButtonsWrapper>
      </MainHeader>

      <Paper className={classes.mainPaper}>
        {/* Filtros */}
        <Paper className={classes.filtersContainer}>
          <Typography variant="h6" gutterBottom>
            Filtros
          </Typography>
          <div className={classes.filterRow}>
            <FormControl variant="outlined" style={{ minWidth: 160 }}>
              <InputLabel>Período</InputLabel>
              <Select
                value={filters.period}
                onChange={(e) => handlePeriodChange(e.target.value)}
                label="Período"
              >
                <MenuItem value="7days">Últimos 7 dias</MenuItem>
                <MenuItem value="30days">Últimos 30 dias</MenuItem>
                <MenuItem value="90days">Últimos 90 dias</MenuItem>
                <MenuItem value="thisMonth">Este mês</MenuItem>
                <MenuItem value="lastMonth">Mês anterior</MenuItem>
                <MenuItem value="custom">Personalizado</MenuItem>
              </Select>
            </FormControl>

            {filters.period === 'custom' && (
              <>
                <DatePicker
                  label="Data inicial"
                  value={filters.dateFrom}
                  onChange={(date) => setFilters(prev => ({ ...prev, dateFrom: date }))}
                  format="dd/MM/yyyy"
                  inputVariant="outlined"
                  style={{ minWidth: 140 }}
                />
                <DatePicker
                  label="Data final"
                  value={filters.dateTo}
                  onChange={(date) => setFilters(prev => ({ ...prev, dateTo: date }))}
                  format="dd/MM/yyyy"
                  inputVariant="outlined"
                  style={{ minWidth: 140 }}
                />
                <Button
                  variant="contained"
                  color="primary"
                  onClick={() => loadStats()}
                  disabled={loading}
                >
                  Aplicar
                </Button>
              </>
            )}
          </div>
        </Paper>

        {loading ? (
          <div className={classes.loadingContainer}>
            <CircularProgress size={60} />
          </div>
        ) : !stats ? (
          <div className={classes.noData}>
            <ShippingIcon style={{ fontSize: 64, marginBottom: 16, opacity: 0.3 }} />
            <Typography variant="h6">
              Nenhum dado encontrado
            </Typography>
          </div>
        ) : (
          <>
            {/* Cards de Estatísticas */}
            <Grid container spacing={3} style={{ marginBottom: 24 }}>
              <Grid item xs={12} sm={6} md={3}>
                <Card className={classes.statsCard}>
                  <CardContent>
                    <div className={classes.statsContent}>
                      <div>
                        <Typography className={classes.statsValue}>
                          {stats.totalOrders}
                        </Typography>
                        <Typography className={classes.statsLabel}>
                          {i18n.t("logistics.stats.totalOrders")}
                        </Typography>
                      </div>
                      <AssignmentIcon className={classes.statsIcon} />
                    </div>
                  </CardContent>
                </Card>
              </Grid>

              <Grid item xs={12} sm={6} md={3}>
                <Card className={classes.statsCard}>
                  <CardContent>
                    <div className={classes.statsContent}>
                      <div>
                        <Typography className={classes.statsValue}>
                          {stats.ordersWithTracking}
                        </Typography>
                        <Typography className={classes.statsLabel}>
                          {i18n.t("logistics.stats.ordersWithTracking")}
                        </Typography>
                      </div>
                      <ShippingIcon className={classes.statsIcon} />
                    </div>
                  </CardContent>
                </Card>
              </Grid>

              <Grid item xs={12} sm={6} md={3}>
                <Card className={classes.statsCard}>
                  <CardContent>
                    <div className={classes.statsContent}>
                      <div>
                        <Typography className={classes.statsValue}>
                          {stats.delivered}
                        </Typography>
                        <Typography className={classes.statsLabel}>
                          {i18n.t("logistics.stats.delivered")}
                        </Typography>
                      </div>
                      <CheckCircleIcon className={classes.statsIcon} />
                    </div>
                  </CardContent>
                </Card>
              </Grid>

              <Grid item xs={12} sm={6} md={3}>
                <Card className={classes.statsCard}>
                  <CardContent>
                    <div className={classes.statsContent}>
                      <div>
                        <Typography className={classes.statsValue}>
                          {stats.averageDeliveryTime.toFixed(1)}
                        </Typography>
                        <Typography className={classes.statsLabel}>
                          {i18n.t("logistics.stats.averageDeliveryTime")} ({i18n.t("logistics.stats.days")})
                        </Typography>
                      </div>
                      <SpeedIcon className={classes.statsIcon} />
                    </div>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>

            {/* Gráficos */}
            <Grid container spacing={3}>
              {/* Performance de Entrega */}
              <Grid item xs={12} md={6}>
                <Card className={classes.chartCard}>
                  <Typography variant="h6" gutterBottom>
                    {i18n.t("logistics.stats.deliveryPerformance")}
                  </Typography>
                  {deliveryStatusData ? (
                    <div className={classes.chartContainer}>
                      <Doughnut
                        data={deliveryStatusData}
                        options={{
                          responsive: true,
                          maintainAspectRatio: false,
                          plugins: {
                            legend: {
                              position: 'bottom',
                            },
                          },
                        }}
                      />
                    </div>
                  ) : (
                    <div className={classes.noData}>
                      <Typography>Sem dados para exibir</Typography>
                    </div>
                  )}
                </Card>
              </Grid>

              {/* Por Provedor */}
              <Grid item xs={12} md={6}>
                <Card className={classes.chartCard}>
                  <Typography variant="h6" gutterBottom>
                    {i18n.t("logistics.stats.byProvider")}
                  </Typography>
                  {providerChartData ? (
                    <div className={classes.chartContainer}>
                      <Doughnut
                        data={providerChartData}
                        options={{
                          responsive: true,
                          maintainAspectRatio: false,
                          plugins: {
                            legend: {
                              position: 'bottom',
                            },
                          },
                        }}
                      />
                    </div>
                  ) : (
                    <div className={classes.noData}>
                      <Typography>Sem dados para exibir</Typography>
                    </div>
                  )}
                </Card>
              </Grid>
            </Grid>

            {/* Performance Detalhada */}
            <Card className={classes.performanceCard} style={{ marginTop: 24 }}>
              <Typography variant="h6" gutterBottom>
                Performance Detalhada
              </Typography>
              <Grid container className={classes.performanceGrid}>
                <Grid item xs={12} sm={4} className={classes.performanceItem}>
                  <Typography className={classes.performanceValue} style={{ color: '#4caf50' }}>
                    {stats.inTransit}
                  </Typography>
                  <Typography className={classes.performanceLabel}>
                    {i18n.t("logistics.stats.inTransit")}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={4} className={classes.performanceItem}>
                  <Typography className={classes.performanceValue} style={{ color: '#ff9800' }}>
                    {stats.pending}
                  </Typography>
                  <Typography className={classes.performanceLabel}>
                    {i18n.t("logistics.stats.pending")}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={4} className={classes.performanceItem}>
                  <Typography className={classes.performanceValue} style={{ color: '#2196f3' }}>
                    {Math.round((stats.ordersWithTracking / stats.totalOrders) * 100)}%
                  </Typography>
                  <Typography className={classes.performanceLabel}>
                    Taxa de Rastreamento
                  </Typography>
                </Grid>
              </Grid>
            </Card>
          </>
        )}
      </Paper>
    </MainContainer>
  );
};

export default LogisticsPage;